import { useEffect, useMemo, useState, useCallback } from "react";
import { useAppContext } from "../../context/AppContext";
import { LiveTrans, ToasterTrans, WorkoutTrans } from "../../utils/translations";
import DropdownButton from "../utilPages/DropdownButton";
import PopupModal from "../utilPages/PopupModal";
import { getLives } from "../../services/practiceService";
import VideoDisplay from "../utilPages/VideoDisplay";
import SaveForLaterButton from "../utilPages/SaveForLaterButton";
import MarkAsCompleteButton from "../utilPages/MarkAsCompleteButton";
import { updateUser } from "../../services/userService";
import { useSearchParams } from "react-router-dom";

const LibraryLivesPage = () => {
  const { language, token, user, setUser, setToaster } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPractice, setCurrentPractice] = useState({});
  const [practices, setPractices] = useState([]);
  const [selectionStates, setSelectionStates] = useState({
    isComplete: false,
    isSaved: false,
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const levelOptions = useMemo(
    () => [
      { key: "", name: WorkoutTrans[language].any },
      { key: "beginner", name: WorkoutTrans[language].beginner },
      { key: "experienced", name: WorkoutTrans[language].experienced },
      { key: "advanced", name: WorkoutTrans[language].advanced },
    ],
    [language]
  );

  const typeOptions = useMemo(
    () => [
      { key: "", name: WorkoutTrans[language].any },
      { key: "recovery", name: WorkoutTrans[language].recovery },
      { key: "progression", name: WorkoutTrans[language].progression },
    ],
    [language]
  );

  const durationOptions = useMemo(
    () => [
      { key: null, name: WorkoutTrans[language].anyFem },
      { key: 15, name: WorkoutTrans[language].fifteen },
      { key: 30, name: WorkoutTrans[language].thirty },
      { key: 60, name: WorkoutTrans[language].sixty },
      { key: 90, name: WorkoutTrans[language].ninety },
    ],
    [language]
  );

  const [selectedLevel, setSelectedLevel] = useState(levelOptions[0]);
  const [selectedType, setSelectedType] = useState(typeOptions[0]);
  const [selectedDuration, setSelectedDuration] = useState(durationOptions[0]);

  const updateUserFavoritesAndCompletions = useCallback(async (practiceId, isSaved, isComplete) => {
    try {
      const updatedUser = await updateUser(token, {
        savedPractices: isSaved
          ? Array.from(new Set([...user.savedPractices, practiceId]))
          : user.savedPractices.filter(id => id !== practiceId),
        completedPractices: isComplete
          ? Array.from(new Set([...user.completedPractices, practiceId]))
          : user.completedPractices.filter(id => id !== practiceId),
      });
      setUser(updatedUser);
    } catch (error) {
      console.error("Error updating user:", error);
    }
  }, [token, user, setUser]);  

  const handleRestrictedAction = (action) => {
    if (!user) {
      setToaster({message: ToasterTrans[language].login, type: "info", show: true});
    } else {
      action();
    }
  };

  const toggleSaved = () => {
    setSelectionStates((prev) => {
      const newSavedState = !prev.isSaved;
      updateUserFavoritesAndCompletions(currentPractice.uuid, newSavedState, prev.isComplete);
      return { ...prev, isSaved: newSavedState };
    });
  };

  const toggleComplete = () => {
    setSelectionStates((prev) => {
      const newCompleteState = !prev.isComplete;
      updateUserFavoritesAndCompletions(currentPractice.uuid, prev.isSaved, newCompleteState);
      return { ...prev, isComplete: newCompleteState };
    });
  };

  const openModal = (practice) => {
    setCurrentPractice(practice);
    setSelectionStates({
      isComplete: user?.completedPractices?.includes(practice.uuid) || false,
      isSaved: user?.savedPractices?.includes(practice.uuid) || false,
    });
    setIsModalOpen(true);
    setSearchParams({ video: practice.uuid });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectionStates({ isComplete: false, isSaved: false });
    setCurrentPractice({});
    setSearchParams({});
  };

  useEffect(() => {
    if (!user || (user.role !== "onlineClub" && user.role !== "admin")) {
      window.location.assign("/library");
    }
    if (!practices.length) {
      (async () => {
        const fetchedPractices = await getLives();
        setPractices(fetchedPractices);

        const practiceId = searchParams.get("video");
        if (practiceId) {
          const practiceToOpen = fetchedPractices.find(practice => practice.uuid === practiceId);
          if (practiceToOpen) {
            openModal(practiceToOpen);
          }
        }
      })();
    }
  }, [practices.length, user]);

  const filteredPractices = useMemo(() => {
    const filtered = practices.filter((practice) => {
      const matchesLevel = selectedLevel.key === "" || (practice.level && practice.level.includes(selectedLevel.key));
      const matchesType = selectedType.key === "" || practice.type === selectedType.key || practice.type.includes(selectedType.key);
      const matchesDuration =
        selectedDuration.key === null ||
        (selectedDuration.key === 15 && practice.duration <= 15) ||
        (selectedDuration.key === 30 && practice.duration > 15 && practice.duration <= 45) ||
        (selectedDuration.key === 60 && practice.duration > 45 && practice.duration <= 75) ||
        (selectedDuration.key === 90 && practice.duration > 75 && practice.duration <= 105);

      return matchesLevel && matchesType && matchesDuration;
    });
    return filtered.sort((a, b) => new Date(b.liveDate || b.createdAt) - new Date(a.liveDate || a.createdAt));
  }, [practices, selectedLevel, selectedType, selectedDuration]);

  const getPracticeVideoUrl = (practice) =>
    practice?.videos?.[language]?.default ||
    practice?.videos?.ru?.default ||
    practice?.videos?.en?.default ||
    practice?.videoURL ||
    "";

  const getPracticeCoverPhotoUrl = (practice) =>
    practice?.coverPhotos?.[language] ||
    practice?.coverPhotos?.ru ||
    practice?.coverPhotos?.en ||
    practice?.coverPhoto ||
    "";

  return (
    <main className="flex min-h-screen flex-col">
      <div className="h-[55vh] w-full overflow-hidden">
        <img
          src="/wallpaper/mat.png"
          alt=""
          className="h-full w-full object-cover object-[center_25%]"
        />
      </div>
      <div className="w-11/12 lg:w-10/12 mx-auto mb-10">
        <h1>{LiveTrans[language].header}</h1>
        <div className="flex flex-col sm:flex-row justify-between items-center mt-5 w-11/12 mx-auto">
          <div className="flex flex-wrap gap-4 sm:gap-14 text-xs lg:text-base w-full justify-around md:justify-start">
            <DropdownButton
              title={WorkoutTrans[language].level}
              options={levelOptions}
              selectedOption={selectedLevel}
              setSelectedOption={setSelectedLevel}
            />
            <DropdownButton
              title={WorkoutTrans[language].type}
              options={typeOptions}
              selectedOption={selectedType}
              setSelectedOption={setSelectedType}
            />
          </div>
          <div className="mt-4 sm:mt-0 text-xs lg:text-base">
            <DropdownButton
              title={WorkoutTrans[language].duration}
              options={durationOptions}
              selectedOption={selectedDuration}
              setSelectedOption={setSelectedDuration}
            />
          </div>
        </div>
        <div className="container mx-auto w-11/12">
          {filteredPractices.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 mt-8">
              {filteredPractices.map((practice, index) => (
                <div key={index}>
                  <img
                    src={getPracticeCoverPhotoUrl(practice)}
                    alt={`Video thumbnail ${index + 1}`}
                    onClick={() => openModal(practice)}
                    className="cursor-pointer m-auto rounded-lg"
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-8">{ToasterTrans[language].comingSoon}</p>
          )}
        </div>
      </div>
      {isModalOpen && (
        <PopupModal onClose={closeModal}>
          <div className="flex flex-col h-full">
            <VideoDisplay videoUrl={getPracticeVideoUrl(currentPractice)} />
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-xs md:text-sm">
              <SaveForLaterButton
                isSaved={selectionStates.isSaved}
                toggleSave={() => handleRestrictedAction(toggleSaved)}
                variant="elevated"
              />
              <MarkAsCompleteButton
                isComplete={selectionStates.isComplete}
                toggleComplete={() => handleRestrictedAction(toggleComplete)}
                variant="elevated"
              />
            </div>
          </div>
        </PopupModal>
      )}
    </main>
  );
};

export default LibraryLivesPage;
